import { ChevronLeft } from 'lucide-react';
import React, { useEffect } from 'react';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';

/* ======================================================
   THEME (same as OptiiiPage)
====================================================== */

const theme = {
  bg: '#07070A',
  bgAlt: 'rgba(255,255,255,0.03)',
  card: 'rgba(255,255,255,0.06)',
  text: '#F5F5F7',
  subText: 'rgba(245,245,247,0.72)',
  accent: '#7C3AED',
  border: 'rgba(255,255,255,0.12)',
};

const GlobalStyle = createGlobalStyle`
  :root { color-scheme: dark; }
  body {
    margin: 0;
    background: ${theme.bg};
    color: ${theme.text};
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
  }
`;

/* ======================================================
   PAGE
====================================================== */

const PrivacyPage = () => {
  useEffect(() => window.scrollTo(0, 0), []);

  const path = window.location.pathname;

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />

      <Page>
        <Nav>
            <NavInner>
                {/* LEFT: Back */}
                <NavLeft onClick={() => window.location.href = '/'}>
                    <ChevronLeft /> Back to Optiii
                </NavLeft>

                {/* RIGHT: Legal links */}
                <NavLinks>
                <NavLink href="/privacy" $active={path === '/privacy'}>
                    Privacy
                </NavLink>
                <NavLink href="/terms" $active={path === '/terms'}>
                    Terms
                </NavLink>
                </NavLinks>
            </NavInner>
            </Nav>

        {/* ===== HERO ===== */}
        <Hero>
          <HeroInner>
            <Eyebrow>Optiii</Eyebrow>
            <HeroTitle>Privacy policy</HeroTitle>
            <HeroSub>
              How we collect, use, protect, and govern information when you use Optiii.
            </HeroSub>
          </HeroInner>
        </Hero>

        {/* ===== CONTENT ===== */}
        <Section>
          <Inner>
            <Card>
              <Paragraph>
                This privacy notice discloses the privacy practices for optiii.com.
              </Paragraph>

              <Paragraph>
                Optiii (“us”, “we”, or “our”) operates the optiii.com website.
              </Paragraph>

              <Paragraph>
                This page informs you of our policies regarding the collection, use,
                and disclosure of personal data when you use our Service and the
                choices you have associated with that data.
              </Paragraph>

              <Paragraph>
                We use your data to provide and improve the Service. By using the
                Service, you agree to the collection and use of information in
                accordance with this policy.
              </Paragraph>

              <Paragraph>
                This privacy notice applies solely to information collected by this
                website. It will notify you of the following:
              </Paragraph>

              <List>
                <li>
                  What personally identifiable information is collected from you
                  through the website, how it is used, and with whom it may be
                  shared.
                </li>
                <li>
                  What choices are available to you regarding the use of your data.
                </li>
                <li>
                  The security procedures in place to protect the misuse of your
                  information.
                </li>
                <li>
                  How you can correct any inaccuracies in the information.
                </li>
              </List>

              <Divider />

              <SectionTitle>Information collection, use, and sharing</SectionTitle>

              <Paragraph>
                We are the sole owners of the information collected on this site.
                We only have access to or collect information that you voluntarily
                give us when using the site, or to those in your organisation that
                have authorised information to be shared with you. We will not sell
                or rent this information to anyone.
              </Paragraph>

              <Paragraph>
                We use your information to provide the services of the site. We will
                not share your information with any third party outside of our
                organisation, other than as necessary to fulfil your request.
              </Paragraph>

              <Paragraph>
                Unless you ask us not to, we may contact you via email in the future
                to tell you about specials, new products or services, or changes to
                this privacy policy.
              </Paragraph>

              <Paragraph>
                We collect statistical information on web traffic using a third-party
                web analytics service. This may use a cookie on your computer, but no
                user-identifiable information is ever shared with this third party,
                other than typical web analytics such as IP address, browser, and
                similar data.
              </Paragraph>

              <Paragraph>
                In no event will your individual data be made available to anyone,
                nor will any personally identifiable information be made available
                or associated with any of the data.
              </Paragraph>

              <Paragraph>
                Information loaded to the site may be used from time to time to
                improve training our proprietary models. We will always de-identify
                and anonymise all information before conducting any model training.
                The resulting models will remain the property of Optiii and be
                retained after you leave.
              </Paragraph>

              <Divider />

              <SectionTitle>Your access to and control over information</SectionTitle>

              <Paragraph>
                At any time, you can ask us to delete information related to your
                account, except where the information has been loaded within an
                organisation. In such cases, we require an administrator to approve
                the deletion of any data that belongs to the company.
              </Paragraph>

              <Paragraph>
                We welcome any requests at{' '}
                <EmailLink href="mailto:team@optiii.com">
                  team@optiii.com
                </EmailLink>
              </Paragraph>

              <Divider />

              <SectionTitle>Security</SectionTitle>

              <Paragraph>
                We take precautions to protect your information. When you submit
                sensitive information via the website, your information is protected
                both online and offline through proprietary techniques.
              </Paragraph>

              <Paragraph>
                Wherever we collect sensitive information, that information is
                encrypted and transmitted to us in a secure way.
              </Paragraph>

              <Paragraph>
                Before using any AI models, we pre-process your information to ensure
                no sensitive or personally identifiable information is included.
              </Paragraph>

              <Paragraph>
                We do store sensitive information as part of our outputs where
                quoting that information is part of the service we provide to you.
                We take a number of precautionary steps to protect your information.
              </Paragraph>

              <Paragraph>
                If you feel that we are not abiding by this privacy policy, please
                contact us immediately via email at{' '}
                <EmailLink href="mailto:team@optiii.com">
                  team@optiii.com
                </EmailLink>
              </Paragraph>
            </Card>
          </Inner>
        </Section>
      </Page>
    </ThemeProvider>
  );
};

export default PrivacyPage;

/* ======================================================
   STYLES (borrowed from OptiiiPage)
====================================================== */

const Page = styled.div``;

const Hero = styled.section`
  padding: 160px 24px 0px;
`;

const HeroInner = styled.div`
  max-width: 980px;
  margin: 0 auto;
`;

const Eyebrow = styled.div`
  font-size: 13px;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.subText};
  margin-bottom: 14px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(42px, 6vw, 64px);
  font-weight: 600;
  margin: 0 0 12px;
`;

const HeroSub = styled.p`
  font-size: 18px;
  color: ${({ theme }) => theme.subText};
  max-width: 720px;
  line-height: 1.7;
`;

const Section = styled.section`
  padding: 80px 24px 140px;
`;

const Inner = styled.div`
  max-width: 980px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 24px;
  padding: 36px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 800;
  margin: 32px 0 14px;
`;

const Paragraph = styled.p`
  font-size: 15px;
  line-height: 1.75;
  color: ${({ theme }) => theme.subText};
  margin: 0 0 14px;
`;

const List = styled.ul`
  padding-left: 20px;
  margin: 12px 0 24px;
  color: ${({ theme }) => theme.subText};

  li {
    margin-bottom: 10px;
    line-height: 1.6;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.border};
  margin: 36px 0;
`;

const EmailLink = styled.a`
  color: #fff;
  text-decoration: none;
  border-bottom: 1px solid rgba(255,255,255,0.35);

  &:hover {
    border-color: #fff;
  }
`;


/* --- Legal Nav --- */

const Nav = styled.div`
  position: fixed;
  top: 16px;
  left: 0;
  right: 0;
  z-index: 90;
  padding: 0 18px;
`;

const NavInner = styled.div`
  max-width: 980px;
  margin: 0 auto;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(14px);
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 999px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const NavLeft = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.subText};
  font-size: 14px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 999px;
  display:flex;
  flex-direction:row;
  align-items:center;
  align-content:center;
  justify-content:center;
  gap:20px;

  &:hover {
    background: rgba(255,255,255,0.06);
    color: ${({ theme }) => theme.text};
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 8px;
`;

const NavLink = styled.a`
  font-size: 14px;
  padding: 8px 14px;
  border-radius: 999px;
  text-decoration: none;
  cursor: pointer;
  color: ${({ theme, $active }) =>
    $active ? '#fff' : theme.subText};
  background: ${({ $active }) =>
    $active ? 'rgba(255,255,255,0.12)' : 'transparent'};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.border : 'transparent'};

  &:hover {
    background: rgba(255,255,255,0.08);
    color: #fff;
  }
`;
